/**
 * PATH: packages/email-hub/src/feature/referrals/useReferralData.ts
 * FILE_ID: EMAILHUB-REFERRAL-DATA-001
 * PURPOSE: Typed query/mutation seam for the target-org gated referral queue.
 * WHY: Cross-org UI must always send an explicit Quotech organisation id.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { BackendSchemas } from '@quotech/api'

import {
  quotechJson,
  resolveQuotechTenantId,
} from '../../_internal/services/quotechApi'

export type OrgEmailReferral = BackendSchemas['OrgEmailReferral']
export type OrgEmailReferralSummary = BackendSchemas['OrgEmailReferralSummary']
export type CrossOrgTransferGrant = BackendSchemas['CrossOrgTransferGrant']
export type OrgEmailReferralTarget = BackendSchemas['OrgEmailReferralTarget']
export type EmailOrgSuggestionDecisionResponse =
  BackendSchemas['EmailOrgSuggestionDecisionResponse']

export const REFERRAL_KEYS = {
  summary: (orgId: number | null) => ['email-referrals', orgId, 'summary'] as const,
  list: (orgId: number | null, status: string) =>
    ['email-referrals', orgId, 'list', status] as const,
  grants: (orgId: number | null) => ['email-referrals', orgId, 'grants'] as const,
  targets: (emailId: string) => ['email-referrals', emailId, 'targets'] as const,
}

function activeOrgId(): number | null {
  return resolveQuotechTenantId()
}

export function useReferralSummary() {
  const orgId = activeOrgId()
  return useQuery({
    queryKey: REFERRAL_KEYS.summary(orgId),
    enabled: orgId != null,
    queryFn: async () => {
      const { data } = await quotechJson<OrgEmailReferralSummary>(
        `/api/email/referrals/summary/?target_org_id=${orgId}`,
      )
      return data ?? {
        pending_count: 0,
        total_count: 0,
        has_transfer_grant: false,
        can_manage_grants: false,
      }
    },
    staleTime: 30_000,
  })
}

export function useReferrals(status = 'pending') {
  const orgId = activeOrgId()
  return useQuery({
    queryKey: REFERRAL_KEYS.list(orgId, status),
    enabled: orgId != null,
    queryFn: async () => {
      const { data } = await quotechJson<OrgEmailReferral[]>(
        `/api/email/referrals/?target_org_id=${orgId}&status=${encodeURIComponent(status)}`,
      )
      return data ?? []
    },
    staleTime: 15_000,
  })
}

export function useReferralDecision() {
  const qc = useQueryClient()
  const orgId = activeOrgId()
  return useMutation({
    mutationFn: async ({
      referralId,
      decision,
    }: {
      referralId: string
      decision: 'accepted' | 'dismissed'
    }) => {
      const { data } = await quotechJson<OrgEmailReferral>(
        `/api/email/referrals/${referralId}/decision/`,
        { method: 'POST', body: { decision } },
      )
      if (!data) throw new Error('The referral decision returned no result.')
      return data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['email-referrals', orgId] })
    },
  })
}

export function useReferralTargets(emailId: string, enabled = true) {
  return useQuery({
    queryKey: REFERRAL_KEYS.targets(emailId),
    enabled: Boolean(emailId) && enabled,
    queryFn: async () => {
      const { data } = await quotechJson<OrgEmailReferralTarget[]>(
        `/api/email/referral-targets/?email_id=${encodeURIComponent(emailId)}`,
      )
      return data ?? []
    },
    staleTime: 30_000,
  })
}

export function useCreateReferral() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      emailId,
      targetOrgId,
      note = '',
    }: {
      emailId: string
      targetOrgId: number
      note?: string
    }) => {
      const { data } = await quotechJson<OrgEmailReferral>(
        '/api/email/referrals/',
        {
          method: 'POST',
          body: {
            email_id: emailId,
            target_org_id: targetOrgId,
            note,
          },
        },
      )
      if (!data) throw new Error('The referral returned no result.')
      return data
    },
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({
        queryKey: REFERRAL_KEYS.targets(variables.emailId),
      })
      void qc.invalidateQueries({ queryKey: ['email-referrals'] })
    },
  })
}

/**
 * Pure transport for one org-suggestion decision (confirm or dismiss).
 *
 * Inputs: email id, the suggested target org id, and the decision verb.
 * Output: the backend decision response. Side effects: one POST. Throws on
 * failure or an empty payload. Exported so provider-less callers (the reader
 * decision runtime) reuse the exact endpoint instead of forking it; the hook
 * below stays the react-query wrapper with cache invalidation.
 */
export async function decideOrgSuggestion({
  emailId,
  targetOrgId,
  decision,
}: {
  emailId: string
  targetOrgId: number
  decision: 'confirm' | 'dismiss'
}): Promise<EmailOrgSuggestionDecisionResponse> {
  const { data } = await quotechJson<EmailOrgSuggestionDecisionResponse>(
    `/api/email/org-suggestions/${emailId}/decision/`,
    {
      method: 'POST',
      body: { target_org_id: targetOrgId, decision },
    },
  )
  if (!data) throw new Error('The suggestion decision returned no result.')
  return data
}

export function useOrgSuggestionDecision() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: decideOrgSuggestion,
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({
        queryKey: REFERRAL_KEYS.targets(variables.emailId),
      })
      void qc.invalidateQueries({ queryKey: ['emails'] })
      void qc.invalidateQueries({ queryKey: ['email-referrals'] })
    },
  })
}

export function useTransferGrants(orgId: number | null) {
  return useQuery({
    queryKey: REFERRAL_KEYS.grants(orgId),
    enabled: orgId != null,
    queryFn: async () => {
      const { data } = await quotechJson<CrossOrgTransferGrant[]>(
        `/api/email/transfer-grants/?target_org_id=${orgId}`,
      )
      return data ?? []
    },
    staleTime: 30_000,
  })
}

export function useGrantTransferAccess(orgId: number | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (userId: number) => {
      if (orgId == null) throw new Error('No target organisation is active.')
      const { data } = await quotechJson<CrossOrgTransferGrant>(
        '/api/email/transfer-grants/',
        {
          method: 'POST',
          body: { target_org_id: orgId, user_id: userId },
        },
      )
      if (!data) throw new Error('Transfer access returned no grant.')
      return data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: REFERRAL_KEYS.grants(orgId) })
      void qc.invalidateQueries({ queryKey: REFERRAL_KEYS.summary(orgId) })
    },
  })
}

export function useRevokeTransferAccess(orgId: number | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (grantId: string) => {
      await quotechJson(`/api/email/transfer-grants/${grantId}/`, {
        method: 'DELETE',
      })
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: REFERRAL_KEYS.grants(orgId) })
      void qc.invalidateQueries({ queryKey: REFERRAL_KEYS.summary(orgId) })
    },
  })
}
